import { useEffect, useContext, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import WishlistButton from '../components/WishlistButton';
import { CartContext } from '../contexts/CartContext';
import productService from '../services/productService';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import { formatPrice, getPrimaryImage, isInStock, getStockDisplay, handleImageError, addCacheBuster } from '../utils/apiHelpers';
import useContentUpdates from '../hooks/useContentUpdates';

const FALLBACK_BACKGROUND = '/assets/img/banner1.1.jpg';

function TagFilterPage() {
  const [searchParams] = useSearchParams();
  const tag = searchParams.get('tag');
  const { addToCart, loading: cartLoading } = useContext(CartContext);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  // Background image state
  const [backgroundUrl, setBackgroundUrl] = useState(FALLBACK_BACKGROUND);

  // Per-product image cache-busting keys for WebSocket updates
  const [productImageKeys, setProductImageKeys] = useState(new Map());

  // Fetch page background from the same source as product-listing
  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      } else {
        setBackgroundUrl(FALLBACK_BACKGROUND);
      }
    } catch (err) {
      console.error('[TagFilterPage] Error fetching background:', err);
      setBackgroundUrl(FALLBACK_BACKGROUND);
    }
  }, []);

  // Fetch products by tag
  const fetchProducts = useCallback(async () => {
    if (!tag) {
      setError('No tag specified');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await productService.getProductsByTag(tag, {
        page: 0,
        size: 12
      });

      if (response.success) {
        setProducts(response.products || []);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to load products');
      }
    } catch (err) {
      console.error('[TagFilterPage] Error:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [tag]);

  // Initial fetch on mount
  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  // Initial fetch when tag changes
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Listen for real-time product updates via WebSocket
  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    },

    onProductUpdated: (productId, sku, name) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[TagFilterPage] Product ${productId} updated: ${name} (${sku}) - refreshing image for this product only`);
      }

      // Only refresh cache for this specific product
      setProductImageKeys(prev => {
        const newMap = new Map(prev);
        newMap.set(productId, Date.now());
        return newMap;
      });

      // Refetch only this specific product to get updated data
      productService.getProduct(productId).then(response => {
        if (response.success && response.product) {
          setProducts(prev =>
            prev.map(p => p.id === productId ? response.product : p)
          );
        }
      }).catch(err => {
        console.error('[TagFilterPage] Error refetching product after update:', err);
        // Fallback: refetch all products if single product fetch fails
        fetchProducts();
      });
    },

    onProductDeleted: (productId) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[TagFilterPage] Product deleted: ${productId}`);
      }
      // Remove product from list immediately
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.warning('A product has been removed', { autoClose: 2000 });
    }
  });

  // Handler to add product to cart
  const handleAddToCart = async (e, productId, productName) => {
    e.preventDefault();
    e.stopPropagation();

    const result = await addToCart(productId, 1);
    if (result.success) {
      toast.success(`${productName} added to cart!`);
    } else {
      toast.error(result.error || 'Failed to add to cart');
    }
  };

  // Format tag for display (capitalize words, replace hyphens)
  const formatTagDisplay = (tagSlug) => {
    if (!tagSlug) return '';
    return tagSlug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Layout>
      {/* Parallax Banner */}
      <ParallaxBanner
        imageUrl={backgroundUrl}
        speed={0.6}
        offset={-100}
        className="paralax-section-slide-data1"
      />

      {/* Tag Filter Page Start */}
      <div className="product-listing">
        <div className="container">
          <div className="row">
            <div className="col-12">
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="mb-4">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/product-listing">Products</Link>
                  </li>
                  {tag && (
                    <li className="breadcrumb-item active" aria-current="page">
                      {formatTagDisplay(tag)}
                    </li>
                  )}
                </ol>
              </nav>

              {/* Page Header */}
              {tag && !error && (
                <div className="product-listing-heading mb-5 text-center">
                  <h1 className="heading">
                    {formatTagDisplay(tag)}
                  </h1>
                  {pagination && !loading && (
                    <p className="para_text">
                      {pagination.total} {pagination.total === 1 ? 'product' : 'products'} found
                    </p>
                  )}
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="alert alert-warning text-center" role="alert">
                  {error}
                  <div className="mt-3">
                    <Link to="/product-listing" className="btn pink-gradient-btn-into-black">
                      View All Collections
                    </Link>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading products tagged with "{formatTagDisplay(tag)}"...</p>
                </div>
              )}

              {/* Empty State */}
              {!loading && !error && products.length === 0 && tag && (
                <div className="text-center py-5">
                  <p className="text-muted mb-4">No collections found with tag "{formatTagDisplay(tag)}".</p>
                  <Link to="/product-listing" className="btn pink-gradient-btn-into-black">
                    View All Collections
                  </Link>
                </div>
              )}

              {/* Products Grid */}
              {!loading && !error && products.length > 0 && (
                <div className="product-list row">
                  {products.map((product) => {
                    // Only cache-bust if product was updated via WebSocket
                    const cacheKey = productImageKeys.get(product.id);

                    return (
                      <div
                        key={product.id}
                        className="col-12 col-md-6 col-lg-4 manage-color-hover wow slideInUp"
                        data-wow-delay=".2s"
                      >
                        <div className="product-item">
                          <div className="item p-item-img">
                            <Link to={`/product-detail/${product.id}`}>
                              <img
                                src={cacheKey ? addCacheBuster(getPrimaryImage(product), cacheKey) : getPrimaryImage(product)}
                                alt={product.name}
                                onError={handleImageError}
                                loading="lazy"
                              />
                          </Link>
                          <div className="text-center d-flex justify-content-center align-items-center">
                            <button
                              className="listing-cart-icon"
                              onClick={(e) => handleAddToCart(e, product.id, product.name)}
                              disabled={cartLoading || !isInStock(product)}
                              aria-label={`Add ${product.name} to cart`}
                            >
                              <i className="fa fa-shopping-cart"></i>
                            </button>
                          </div>
                          {/* Wishlist button - top left */}
                          <div
                            className="position-absolute"
                            style={{top: '10px', left: '10px', zIndex: 10}}
                          >
                            <WishlistButton
                              productId={product.id}
                              className="product-card-wishlist-btn"
                            />
                          </div>
                          {/* Stock badge */}
                          {(() => {
                            const stockInfo = getStockDisplay(product.stock, product.stockStatus);
                            // Only show badge for out of stock or low stock
                            if (stockInfo.className === 'in-stock') return null;
                            return (
                              <span
                                className={`badge position-absolute stock-badge ${stockInfo.className}`}
                                style={{top: '10px', right: '10px'}}
                              >
                                {stockInfo.className === 'out-of-stock' ? 'Out of Stock' : `Only ${product.stock} left!`}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="p-item-detail">
                        <h4 className="text-center p-item-name">
                          <Link to={`/product-detail/${product.id}`}>
                            {product.name}
                          </Link>
                        </h4>
                        <p className="text-center p-item-price">
                          {formatPrice(product.price)}
                          {product.compareAtPrice && (
                            <span className="text-muted text-decoration-line-through ms-2" style={{fontSize: '0.85em'}}>
                              {formatPrice(product.compareAtPrice)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
      {/* Tag Filter Page End */}
    </Layout>
  );
}

export default TagFilterPage;
